<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import type { Brand } from '@/stores/admin'
import { slugify } from '@/lib/utils'
import { brandCreateSchema, brandUpdateSchema } from '@/views/forms/schema'
import type { FormSubmitEvent } from '@nuxt/ui'
import type { z } from 'zod'

const props = defineProps<{
  brand: Brand | null
}>()

const emit = defineEmits<{
  submit: [
    data: {
      name: string
      slug?: string
      website?: string | null
      logo_file?: File
      delete_logo?: boolean
    },
  ]
  cancel: []
}>()

const pendingLogoDelete = ref(false)

const isEditMode = computed(() => !!props.brand)

const state = ref({
  name: props.brand?.name || '',
  slug: props.brand?.slug || '',
  website: props.brand?.website || '',
  logo_file: undefined as File | undefined,
})

watch(
  () => props.brand,
  (newBrand) => {
    state.value = {
      name: newBrand?.name || '',
      slug: newBrand?.slug || '',
      website: newBrand?.website || '',
      logo_file: undefined,
    }
    pendingLogoDelete.value = false
  },
  { immediate: true },
)

// Clear delete intent when new file is selected
watch(
  () => state.value.logo_file,
  (newFile) => {
    if (newFile) pendingLogoDelete.value = false
  },
)

watch(
  () => state.value.name,
  (newName) => {
    if (!isEditMode.value && newName) {
      state.value.slug = slugify(newName)
    }
  },
)

const schema = computed(() => (isEditMode.value ? brandUpdateSchema : brandCreateSchema))

function onSubmit(
  event: FormSubmitEvent<z.output<typeof brandCreateSchema | typeof brandUpdateSchema>>,
) {
  emit('submit', {
    name: event.data.name,
    slug: isEditMode.value ? undefined : state.value.slug,
    website: state.value.website || null,
    logo_file: state.value.logo_file,
    delete_logo: pendingLogoDelete.value,
  })
  // Reset after emit to prevent double-delete on retry
  pendingLogoDelete.value = false
  state.value.logo_file = undefined
}

const isSubmitDisabled = computed(() => !state.value.name.trim())

const hasChanges = computed(() => {
  if (!isEditMode.value) return !!state.value.name.trim() // Create mode: has changes if name entered
  // Compare website with null-normalization to match how we submit (empty string → null)
  const currentWebsite = state.value.website || null
  return (
    state.value.name !== props.brand?.name ||
    currentWebsite !== props.brand?.website ||
    !!state.value.logo_file ||
    pendingLogoDelete.value
  )
})
</script>

<template>
  <div class="p-4 space-y-6">
    <UForm :schema="schema" :state="state" class="space-y-4" @submit="onSubmit">
      <UFormField label="Name" name="name" required>
        <UInput v-model="state.name" placeholder="Brand name" class="w-full" />
      </UFormField>

      <UFormField label="Slug" name="slug" :required="!isEditMode">
        <UInput
          v-model="state.slug"
          :disabled="isEditMode"
          placeholder="brand-slug"
          class="w-full font-mono"
        />
        <template #hint v-if="isEditMode">
          <span class="text-xs text-muted">Slug cannot be changed after creation</span>
        </template>
        <template #hint v-else>
          <span class="text-xs text-muted">Auto-generated from name. You can customize it.</span>
        </template>
      </UFormField>

      <UFormField label="Website" name="website">
        <UInput v-model="state.website" placeholder="example.com" class="w-full" />
        <template #hint>
          <span class="text-xs text-muted">Brand website (without http://)</span>
        </template>
      </UFormField>

      <UFormField
        label="Logo"
        name="logo_file"
        class="flex max-sm:flex-col justify-between items-start gap-4"
      >
        <UFileUpload v-model="state.logo_file" accept="image/*" />
        <template #description>
          Pick a square logo without background, in png or jpeg format.
          <br />
          <br />
          <div v-if="brand?.logo_url && !pendingLogoDelete" class="flex items-center gap-2">
            <span>Current logo:</span>
            <UAvatar :src="brand.logo_url" alt="Brand logo" />
            <UButton
              icon="i-lucide-trash-2"
              color="error"
              variant="ghost"
              size="xs"
              @click="pendingLogoDelete = true"
            />
          </div>
          <div v-else-if="pendingLogoDelete" class="flex items-center gap-2">
            <span class="text-error">Logo will be deleted</span>
            <UButton
              icon="i-lucide-undo"
              color="neutral"
              variant="ghost"
              size="xs"
              @click="pendingLogoDelete = false"
            >
              Undo
            </UButton>
          </div>
        </template>
      </UFormField>

      <div class="flex justify-end gap-2 pt-4">
        <UButton type="button" color="neutral" variant="soft" @click="emit('cancel')">
          Cancel
        </UButton>
        <UButton
          type="submit"
          color="primary"
          :variant="hasChanges ? 'solid' : 'soft'"
          :disabled="isSubmitDisabled || !hasChanges"
          :class="{ 'animate-pulse': hasChanges }"
        >
          {{ isEditMode ? 'Update Brand' : 'Create Brand' }}
        </UButton>
      </div>
    </UForm>
  </div>
</template>
